import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getFanTypes, getFanToleranceClasses } from "./Data";
import { getMassUnits } from "../../../../utils/helpers/tolerance/GeneralHelper";
import { useMeasureInUnits } from "../../../../hooks/useMeasureInUnits";
import AccuracyClassSelector from "../../../../components/Widgets/Tolerance/AccuracyClassSelector/AccuracyClassSelector";
import HelpInput from "../../../../components/Inputs/HelpInput/HelpInput";
import { FanTolerance } from "./FanTolerance";
import InputStack from "../../../../components/Styled/Stack/InputStack";
import PageContainerBox from "../../../../components/Styled/Box/PageContainerBox";

export default function FanToleranceWidget() {
  const { t } = useTranslation();

  const [typeSelected, setTypeSelected] = useState(0);
  const [toleranceClass, setToleranceClass] = useState(null);
  const [frequency, setFrequency] = useState();

  const partMassUnits = useMemo(() => getMassUnits(t).slice(1, 4), [t]);

  const [
    massInKg,
    mass,
    setMass /* unitsOfMass, massUnit, handleMassUnitChange */,
  ] = useMeasureInUnits(null, partMassUnits[1]);

  const selectorData = useMemo(() => {
    switch (typeSelected) {
      case 0:
        return getFanToleranceClasses(t);
      case 1:
        return getFanTypes(t);
      default:
        return [];
    }
  }, [t, typeSelected]);

  const isDisabled = useMemo(() => {
    return !toleranceClass || toleranceClass?.value === 0;
  }, [toleranceClass]);

  const isSearchable = useMemo(() => typeSelected === 1, [typeSelected]);

  const selectorDefaultLabel = useMemo(() => {
    switch (typeSelected) {
      case 0:
        return t(
          "pages.fanTolerance.components.toleranceClassSelector.options.byFanClass.selectorLabel"
        );
      case 1:
        return t(
          "pages.fanTolerance.components.toleranceClassSelector.options.byFanApplication.selectorLabel"
        );
      default:
        return "";
    }
  }, [typeSelected, t]);

  const handleFrequencyChange = useCallback((event) => {
    const value = +event.target.value;
    if (value >= 0) {
      setFrequency(value);
    } else {
      setFrequency(0);
    }
  }, []);

  const handleMassChange = useCallback(
    (event) => {
      const value = +event.target.value;
      if (value >= 0) {
        setMass(value);
      } else {
        setMass(0);
      }
    },
    [setMass]
  );

  const handleIsFanSelectedChange = useCallback(
    (event) => {
      setTypeSelected(+event.target.value);

      switch (+event.target.value) {
        case 0: {
          setToleranceClass({
            label: t(
              "pages.fanTolerance.components.toleranceClassSelector.options.byFanClass.selectorLabel"
            ),
            value: 0,
          });
          break;
        }
        case 1: {
          setToleranceClass({
            label: t(
              "pages.fanTolerance.components.toleranceClassSelector.options.byFanApplication.selectorLabel"
            ),
            value: 0,
          });
          break;
        }
        default:
          setToleranceClass(null);
          break;
      }
    },
    [t]
  );

  return (
    <PageContainerBox>
      <Typography variant="h4" sx={{ my: 2 }}>
        {t("pages.fanTolerance.title")}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {t("pages.fanTolerance.components.toleranceClassSelector.title")}
      </Typography>
      <RadioGroup row value={typeSelected} onChange={handleIsFanSelectedChange}>
        <FormControlLabel
          value={0}
          control={<Radio />}
          label={t(
            "pages.fanTolerance.components.toleranceClassSelector.options.byFanClass.label"
          )}
        />
        <FormControlLabel
          value={1}
          control={<Radio />}
          label={t(
            "pages.fanTolerance.components.toleranceClassSelector.options.byFanApplication.label"
          )}
        />
      </RadioGroup>
      <AccuracyClassSelector
        toleranceClasses={selectorData}
        onChange={setToleranceClass}
        defaultLabel={selectorDefaultLabel}
        isSearchable={isSearchable}
        value={toleranceClass}
      />
      {/* Inputs for Frequency and Mass */}
      <Typography variant="h6" sx={{ my: 2 }}>
        {t("pages.fanTolerance.components.massAndFrequency.title")}
      </Typography>
      <InputStack>
        <HelpInput
          label={`m, ${t("units.mass.kg")}`}
          type="number"
          variant="outlined"
          fullWidth
          value={mass || ""}
          onChange={handleMassChange}
          helpButtonText={t(
            "pages.fanTolerance.components.massAndFrequency.mass.helpButtonText"
          )}
          disabled={isDisabled}
        />
        <HelpInput
          label={<div>v, {t("units.rotationSpeed.rpm")}</div>}
          type="number"
          variant="outlined"
          fullWidth
          value={frequency || ""}
          onChange={handleFrequencyChange}
          helpButtonText={t(
            "pages.fanTolerance.components.massAndFrequency.frequency.helpButtonText",
          )}
          disabled={isDisabled}
        />
      </InputStack>
      <FanTolerance
        toleranceClass={toleranceClass}
        isDisabled={isDisabled}
        mass={massInKg}
        frequency={frequency}
      />
    </PageContainerBox>
  );
}
